export function temPermissaoAdminSeo(claims?: Record<string, unknown>): boolean {
  return claims?.admin === true || (typeof claims?.email === "string"
    && claims.email.trim().toLowerCase() === "tgloureiro@gmail.com");
}

export interface RegistroSeoGenerico {
  id: string;
  status?: string;
  iniciadoEm?: string;
  criadoEm?: string;
  coletadoEm?: string;
  atualizadoEm?: string;
  materialidade?: number;
}

export interface ColecoesPainelSeo {
  execucoes: RegistroSeoGenerico[];
  entradas?: RegistroSeoGenerico[];
  auditoria?: RegistroSeoGenerico[];
  oportunidades: RegistroSeoGenerico[];
  conteudos: RegistroSeoGenerico[];
  noticias: RegistroSeoGenerico[];
  imagens: RegistroSeoGenerico[];
  experimentos: RegistroSeoGenerico[];
}

export interface ResumoPainelSeo {
  ultimaExecucao: string | null;
  execucoesComFalha: number;
  oportunidadesAbertas: number;
  conteudosPendentes: number;
  noticiasUrgentes: number;
  imagensPendentes: number;
  experimentosPendentes: number;
}

function instante(item: RegistroSeoGenerico): string {
  return item.atualizadoEm ?? item.iniciadoEm ?? item.criadoEm ?? item.coletadoEm ?? "";
}

export function ordenarPorAtualizacao<T extends RegistroSeoGenerico>(registros: T[]): T[] {
  return [...registros].sort((a, b) => instante(b).localeCompare(instante(a)));
}

export function entradasDaExecucaoSeo<T extends Record<string, unknown>>(
  entradas: readonly T[],
  execucaoId: string,
): T[] {
  return entradas.filter((item) => item["execucaoId"] === execucaoId);
}

export function montarResumoSeo(colecoes: ColecoesPainelSeo): ResumoPainelSeo {
  return {
    ultimaExecucao: ordenarPorAtualizacao(colecoes.execucoes)[0]?.iniciadoEm ?? null,
    execucoesComFalha: colecoes.execucoes.filter((item) => item.status === "falhou" || item.status?.startsWith("blocked_")).length,
    oportunidadesAbertas: colecoes.oportunidades.filter((item) =>
      item.status === "aberta" || item.status === "draft_pending_review").length,
    conteudosPendentes: colecoes.conteudos.filter((item) => item.status === "draft_pending_review").length,
    noticiasUrgentes: colecoes.noticias.filter((item) =>
      ["pendente_avaliacao", "post_individual", "atualizar_conteudo", "incluir_digest"]
        .includes(item.status ?? "") && (item.materialidade ?? 0) >= 80).length,
    imagensPendentes: colecoes.imagens.filter((item) =>
      item.status === "pendente_aprovacao" || item.status === "aguardando_aprovacao").length,
    experimentosPendentes: colecoes.experimentos.filter((item) =>
      item.status === "planejado" || item.status === "completed_pending_measurement").length,
  };
}

export function montarSolicitacaoImagemSeo(
  conteudo: RegistroSeoGenerico & Record<string, unknown>,
  objetivo: string,
) {
  const execucaoId = typeof conteudo.execucaoId === "string" ? conteudo.execucaoId.trim() : "";
  const titulo = typeof conteudo.titulo === "string" ? conteudo.titulo.trim() : "";
  const objetivoLimpo = objetivo.trim();
  if (!conteudo.id || !execucaoId || !titulo || objetivoLimpo.length < 12) {
    throw new Error("conteúdo ou objetivo de imagem incompleto");
  }
  return {
    conteudoId: conteudo.id,
    execucaoId,
    tipo: "cabecalho" as const,
    titulo,
    objetivo: objetivoLimpo,
    textoAlternativo: `Ilustração do artigo ${titulo}`.slice(0, 300),
  };
}

export function montarComandoSeo(acao: string, alvoId: string) {
  return { acao, alvoId };
}

export function mensagemErroLeituraSeo(colecao: string, erro: unknown): string {
  const codigo = erro && typeof erro === "object" && "code" in erro ? erro.code : null;
  const orientacoes: Record<string, string> = {
    "permission-denied": "Leitura negada pelas regras de permissão.",
    "unauthenticated": "Autenticação expirada. Entre novamente.",
    "failed-precondition": "A consulta exige uma configuração ou índice do Firestore.",
    "unavailable": "Firestore indisponível. Verifique a conexão e tente novamente.",
    "resource-exhausted": "Limite de uso do Firestore atingido.",
  };
  const seguro = typeof codigo === "string" && Object.hasOwn(orientacoes, codigo)
    ? codigo : "desconhecido";
  return `Falha ao ler ${colecao} (${seguro}). ${orientacoes[seguro] ?? "Não foi possível concluir a consulta."}`;
}

export function acoesRevisaoSeo(
  tipo: "conteudo" | "imagem" | "oportunidade",
  status: unknown,
): { aprovar: string; rejeitar: string } | null {
  const pendente = tipo === "imagem"
    ? status === "pendente_aprovacao" || status === "aguardando_aprovacao"
    : status === "draft_pending_review";
  if (!pendente) return null;
  return {
    aprovar: `aprovar_${tipo}`,
    rejeitar: `rejeitar_${tipo}`,
  };
}

const CHAVE_SENSIVEL = /authorization|apikey|secret|password|token|connectionstring|privatekey|credential|senha|cookie/i;

export function redigirTextoSeo(texto: string): string {
  return texto
    .replace(/-----BEGIN [A-Z ]*PRIVATE KEY-----[\s\S]*?-----END [A-Z ]*PRIVATE KEY-----/g, "[redigido]")
    .replace(/\b(Bearer|Basic)\s+[^\s,;"']+/gi, "$1 [redigido]")
    .replace(/\b((?:access[-_]?token|refresh[-_]?token|api[-_]?key|client[-_]?secret|password|senha|token|credential|authorization)\s*["']?\s*[:=]\s*["']?)[^\s&;,"'}]+/gi, "$1[redigido]")
    .replace(/\bsk-[A-Za-z0-9_-]{16,}/g, "[redigido]")
    .replace(/\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g, "[redigido]");
}

export function serializarRegistroSeo(registro: unknown): string {
  return JSON.stringify(registro, (chave, valor) =>
    CHAVE_SENSIVEL.test(chave.replace(/[-_]/g, "")) ? "[redigido]"
      : typeof valor === "string" ? redigirTextoSeo(valor) : valor, 2);
}
